"use client";

/**
 * Память чата помощника.
 *
 * Зачем отдельный модуль. `/desk` — одна страница с разделами в состоянии React
 * (`useState<Section>`), а не маршрутами. Переход «Опердир → Главная» РАЗМОНТИРУЕТ
 * `InsightsSection`, вместе с ним `AiChat`, и вся переписка пропадала: человек
 * уходил посмотреть цифру на Главной и возвращался к пустому экрану (Марат,
 * 19.09). Состояние поэтому живёт вне компонента.
 *
 * Два слоя, и оба нужны:
 *  1. Модульная переменная `mem` — переход между разделами. Мгновенно, без
 *     сериализации, переживает размонтирование внутри одной вкладки.
 *  2. localStorage — перезагрузка вкладки и возврат на следующий день.
 *
 * Ключ включает ресторан и логин: на сетевом домене хранилище общее для всех
 * ресторанов сети, а у второго человека за тем же ноутбуком не должно быть
 * чужой переписки с именами и выручкой.
 *
 * В БД переписка НЕ пишется намеренно. Правило проекта: ИИ ничего не пишет в
 * базу без подтверждения человеком, а чат — сценарий только на чтение. Память
 * здесь — удобство клиента, а не документ.
 */

import { SURFACE_SEGMENTS } from "@/lib/network/domain";

// Режимы разговора перечислены в `chat-modes`: там же записано, какие срезы за
// каждым стоят и какие чипы-подсказки он предлагает. Тип берём оттуда, чтобы
// режим нельзя было завести в одном месте и забыть в другом.
import type { ChatMode } from "@/lib/ai/chat-modes";
export type { ChatMode };

export type ChatMsg = {
  role: "me" | "ai";
  text: string;
  mode?: ChatMode;
  failed?: boolean;
  basis?: string[];
  /** Кандидаты при неоднозначном имени: показываем кнопками, выбор продолжает вопрос. */
  people?: { id: string; name: string; role?: string | null }[];
  /** Исходный вопрос — чтобы «Повторить» и выбор сотрудника не заставляли печатать заново. */
  ask?: string;
  /** Готовые вопросы одним нажатием (после приветствия). */
  suggestions?: string[];
  /**
   * Черновики задач, разобранные из поручения.
   *
   * Лежат В ЛЕНТЕ, а не в отдельном состоянии: поручение — часть разговора, и
   * при возврате в раздел карточка обязана остаться там же, где её оставили.
   * Созданные задачи помечаются createdId, чтобы кнопка не предложила
   * поставить их второй раз после перезагрузки.
   */
  tasks?: ChatTask[];
  /** Черновики действий: стоп-лист, возврат в продажу, заявка (Э6). */
  actions?: ChatAction[];
  at?: number;
};

export type ChatAssignee =
  | { kind: "user"; user_id: string; name: string; role: string }
  | { kind: "role"; role_key: string; role_name: string; count: number }
  | { kind: "ambiguous"; query: string; options: string[]; people: { id: string; name: string; role: string }[] }
  | { kind: "unknown"; query: string | null };

export type ChatTask = {
  title: string;
  description: string | null;
  due_at: string | null;
  proof_kind: string;
  repeat: { freq: string; weekdays: number[]; month_day: number | null; human: string } | null;
  assignee: ChatAssignee;
  ready: boolean;
  /** Проставляется после успешного создания: задача уже есть, второй раз не ставим. */
  createdId?: string;
};

/**
 * Черновик ДЕЙСТВИЯ из разговора (Э6): стоп-лист и заявка.
 *
 * Лежит в той же памяти вкладки, что и черновики задач, и по той же причине:
 * человек ушёл в другой раздел и вернулся — карточка должна быть на месте, в
 * том же состоянии («уже сделано» или «ещё нет»).
 */
//
// С 06.10 вид один на все поручения (премия, смена, списание, стоп-лист…):
// сервер присылает строки «что уйдёт» и обычные действия API (`calls`).
// Записи из памяти до 06.10 полей `lines`/`calls` не имеют — карточка
// показывает их без кнопки.
export type ChatAction = {
  kind: string;
  title: string;
  lines?: { label: string; value: string }[];
  note?: string | null;
  verb?: string;
  doneText?: string;
  calls?: { action: string; payload: Record<string, unknown> }[];
  photo?: { required: boolean };
  done?: boolean;
};

export type ChatState = {
  msgs: ChatMsg[];
  mode: ChatMode | null;
  draft: string;
};

/** Префикс всех ключей переписки. Экспортируется: по нему чистит выход. */
export const CHAT_PREFIX = "mise_ai_chat";
const PREFIX = CHAT_PREFIX;
/** Сколько сообщений помним. Потолок нужен: лента растёт, квота localStorage — нет. */
const MAX_MSGS = 80;
/** Потолок на запись. 300 КБ с запасом влезают в браузерные 5 МБ на домен. */
const MAX_BYTES = 300_000;
/** Через две недели молчания переписка протухает: возвращаться к ней уже незачем. */
const TTL_MS = 14 * 24 * 60 * 60 * 1000;

const EMPTY: ChatState = { msgs: [], mode: null, draft: "" };

let mem: { key: string; state: ChatState } | null = null;

function ls(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Приватный режим Safari бросает на самом обращении к localStorage.
    return null;
  }
}

/**
 * Ключ памяти. Считается на каждом обращении, а не один раз при загрузке
 * модуля: переключение ресторана в шапке меняет его без перезагрузки страницы.
 */
function keyOf(): string {
  if (typeof window === "undefined") return PREFIX;
  let who = "";
  let where = "";
  try {
    who = window.localStorage.getItem("mise_login") ?? "";
    // ⚠️ Список поверхностей берём ОБЩИЙ (SURFACE_SEGMENTS), а не свой из двух
    // строк. Свой знал только «desk» и «app», а поверхностей больше; на
    // сетевом домене первый сегмент пути — это ресторан, и если он не узнан,
    // ключ схлопывался до хоста папки. Тогда у одного логина переписки двух
    // ресторанов сети писались в ОДИН ключ — имена сотрудников и выручка
    // соседнего заведения оказывались в ленте (аудит 19.09).
    const seg = (window.location.pathname.split("/")[1] ?? "").toLowerCase();
    where = seg && !SURFACE_SEGMENTS.has(seg) ? seg : window.location.hostname.split(".")[0] ?? "";
  } catch {
    /* см. ls(): хранилище может быть недоступно */
  }
  return `${PREFIX}:${where}:${who}`;
}

function sane(raw: unknown): ChatState {
  if (!raw || typeof raw !== "object") return EMPTY;
  const o = raw as Record<string, unknown>;
  if (typeof o.at === "number" && Date.now() - o.at > TTL_MS) return EMPTY;
  const msgs = Array.isArray(o.msgs)
    ? (o.msgs.filter(
        (m) =>
          m &&
          typeof m === "object" &&
          ((m as ChatMsg).role === "me" || (m as ChatMsg).role === "ai") &&
          typeof (m as ChatMsg).text === "string",
      ) as ChatMsg[])
    : [];
  const mode = o.mode === "ttk" || o.mode === "shifts" || o.mode === "foodcost" ? o.mode : null;
  return {
    msgs: msgs.slice(-MAX_MSGS),
    mode,
    draft: typeof o.draft === "string" ? o.draft.slice(0, 4000) : "",
  };
}

/** Прочитать переписку. Сначала память вкладки, затем хранилище. */
export function readChat(): ChatState {
  const key = keyOf();
  if (mem && mem.key === key) return mem.state;
  const s = ls();
  if (!s) {
    mem = { key, state: EMPTY };
    return EMPTY;
  }
  let state = EMPTY;
  try {
    const raw = s.getItem(key);
    if (raw) state = sane(JSON.parse(raw));
  } catch {
    // Битая запись — не повод ронять экран: начинаем с чистого листа.
    state = EMPTY;
  }
  mem = { key, state };
  return state;
}

/** Сохранить переписку. Тихо переживает переполнение квоты. */
export function writeChat(state: ChatState): void {
  const key = keyOf();
  const trimmed: ChatState = { ...state, msgs: state.msgs.slice(-MAX_MSGS) };
  mem = { key, state: trimmed };
  const s = ls();
  if (!s) return;
  try {
    let payload = JSON.stringify({ ...trimmed, at: Date.now() });
    // Длинные ответы модели могут распухнуть: режем ленту с головы, пока влезает.
    let msgs = trimmed.msgs;
    while (payload.length > MAX_BYTES && msgs.length > 2) {
      msgs = msgs.slice(Math.ceil(msgs.length / 2));
      payload = JSON.stringify({ ...trimmed, msgs, at: Date.now() });
    }
    s.setItem(key, payload);
  } catch {
    // QuotaExceeded или приватный режим. Память вкладки уже обновлена — переход
    // между разделами всё равно работает, теряется только переживание перезагрузки.
  }
}

/** Забыть переписку текущего человека (кнопка «Очистить»). */
export function clearChat(): void {
  const key = keyOf();
  mem = { key, state: EMPTY };
  try {
    ls()?.removeItem(key);
  } catch {
    /* хранилище недоступно — достаточно очистки памяти вкладки */
  }
}

/**
 * Стереть переписку ВСЕХ ключей. Зовётся при выходе: за одним ноутбуком
 * работают разные люди, и следующий не должен увидеть чужие числа.
 */
export function purgeAllChats(): void {
  // Память вкладки обнуляется ОБЯЗАТЕЛЬНО: без этого выход и вход под тем же
  // логином без перезагрузки воскрешали «стёртую» переписку (ревью 19.09).
  mem = null;
  const s = ls();
  if (!s) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k && k.startsWith(PREFIX)) doomed.push(k);
    }
    for (const k of doomed) s.removeItem(k);
  } catch {
    /* хранилище недоступно */
  }
}

/**
 * Что отправить серверу как ход разговора.
 *
 * Берём последние реплики и только их текст: карточки, кандидаты и черновики
 * серверу не нужны — он и так знает, что предлагал. Длину режем здесь же,
 * чтобы не гонять по сети ленту целиком.
 *
 * ⚠️ Это ПОДСКАЗКА модели, а не источник прав: сервер по истории никаких
 * решений о доступе не принимает (см. askAssistant).
 */
export function historyFor(msgs: ChatMsg[], turns = 6): { role: "me" | "ai"; text: string }[] {
  return msgs
    .filter((m) => typeof m.text === "string" && m.text.trim() !== "")
    .slice(-turns)
    .map((m) => ({ role: m.role, text: m.text.slice(0, 600) }));
}
